import React, { useState, useEffect, ReactNode } from "react"
import { Helmet } from "react-helmet"
import Layout from "../Layout"
import Header from "../Header"
import { gradientLightPink } from "../../ds/gradients.module.css"
import EmailPopup from "./EmailPopup"
import { hasReportAccess, submitEmailForReportAccess } from "./utils"
import "./styles/reportStyles.css"

interface ReportLayoutProps {
  children: ReactNode;
  title: string;
  description: string;
  reportId: string;
  reportTitle: string;
  reportSubtitle: string;
  meta?: { name: string; content: string }[];
  showHeader?: boolean;
  showPrintButton?: boolean;
  showShareButton?: boolean;
}

/**
 * A shared layout component for all reports
 * Handles email collection, print/share buttons, and common styling
 */
const ReportLayout: React.FC<ReportLayoutProps> = ({
  children,
  title,
  description,
  reportId,
  reportTitle,
  reportSubtitle,
  meta = [],
  showHeader = true,
  showPrintButton = true,
  showShareButton = true
}) => {
  // State for email collection
  const [showEmailPopup, setShowEmailPopup] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)
  
  // Check if user has already submitted email for this specific report
  useEffect(() => {
    const hasSubmittedEmailForReport = hasReportAccess(reportId)
    if (hasSubmittedEmailForReport) {
      setShowEmailPopup(false)
      setHasAccess(true)
    }
  }, [reportId])
  
  // Handle email submission
  const handleEmailSubmit = async (email: string) => {
    const success = await submitEmailForReportAccess(reportId, email)
    
    if (success) {
      setShowEmailPopup(false)
      setHasAccess(true)
    } else {
      alert('There was an error processing your request. Please try again.');
    }
  }

  // Handle share button click
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: description,
        url: window.location.href
      }).catch(err => {
        console.error("Share failed:", err);
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      const dummy = document.createElement("textarea");
      dummy.value = window.location.href;
      document.body.appendChild(dummy);
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
      alert("Link copied to clipboard!");
    }
  }

  return (
    <Layout
      title={title}
      description={description}
      meta={meta}
    >
      {/* Email collection popup */}
      {showEmailPopup && (
        <EmailPopup 
          onSubmit={handleEmailSubmit} 
          reportName={reportTitle}
        />
      )}

      {showHeader && (
        <div className="no-print">
          <div className={gradientLightPink}>
            <Header />
            <div className="pm-container py-10 text-center">
              <h1 className="font-bold text-2xl md:text-4xl" style={{ color: 'var(--color-blue-dark, #1F0F61)' }}>
                {reportTitle}
              </h1>
              <p className="text-gray-600 mt-2">
                {reportSubtitle}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className={`report-container ${!hasAccess ? 'blur-content' : ''}`}>
        {children}
      </div>
      
      {hasAccess && (showPrintButton || showShareButton) && (
        <div className="no-print pm-container py-10 text-center">
          <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
            {showPrintButton && (
              <button
                onClick={() => window.print()}
                className="print-btn"
              >
                Print Report
              </button>
            )}
            
            {showShareButton && (
              <button
                onClick={handleShare}
                className="print-btn"
                style={{ background: "#4CAF50" }}
              >
                Share Report
              </button>
            )}
          </div>
        </div>
      )}
    </Layout>
  )
}

export default ReportLayout